import styled, { css, keyframes } from 'styled-components';
import { palette } from 'styled-tools';

import { TextButton } from '@calm-web/design-system';

import { ROW_BADGE_BACKGROUND } from '@/components/app/cards/ContentChip/styles';

export const ROW_DIVIDER = '1px solid rgba(255, 255, 255, 0.1)';

export const Wrapper = styled.li`
	align-items: center;
	border-top: ${ROW_DIVIDER};
	display: flex;
	gap: 12px;
	margin: 0 auto;
	padding: 14px 0;
	z-index: 0;

	&:first-child {
		border-top: 0;
	}
`;

export const ClickableWrapper = styled(TextButton).attrs({
	textColor: 'white',
})<{ $isDisabled?: boolean }>`
	cursor: ${p => (p.$isDisabled ? 'default' : 'pointer')};
	opacity: ${p => (p.$isDisabled ? 0.5 : 1)};
	flex: 1;
	text-decoration: none;
`;

export const RowContent = styled.div`
	align-items: center;
	display: flex;
	gap: 12px;
	width: 100%;
`;

export const TextBlock = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 4px;
	text-align: left;
	width: 100%;
`;

export const Title = styled.div`
	align-items: center;
	color: ${palette('white')};
	display: flex;
	font-size: 14px;
	font-weight: 600;
	gap: 6px;
`;

export const Description = styled.div`
	color: ${palette('white')};
	font-size: 13px;
	font-weight: 600;
`;

export const Duration = styled.div`
	color: ${palette('white')};
	font-size: 12px;
	font-weight: 500;
`;

// iOS draws these badges 18pt across. The icon assets keep an 18% inset inside their viewBox, so
// the box is enlarged to 22px and pulled back by the inset to leave an 18px visible circle.
export const StatusGlyph = styled.svg`
	flex: 0 0 22px;
	height: 22px;
	margin: -2px;
	width: 22px;

	& path {
		fill: ${palette('white')};
	}
`;

export const TrailingSlot = styled.div`
	align-items: center;
	display: flex;
	flex-shrink: 0;
	gap: 4px;
`;

const rise = keyframes`
	from { height: 2px; }
	to { height: var(--bar-height); }
`;

// iOS PlayingWaveformBadge: three 2pt bars 4pt apart in a capsule padded 8pt by 4pt, over the
// atmospheric theme's thin material. At rest the bars are 2pt dots. While playing each rises to
// its own height between 10 and 13pt, over 0.78 to 0.9s, started up to 0.25s apart.
export const Waveform = styled.div<{ $isPlaying: boolean }>`
	align-items: flex-end;
	backdrop-filter: blur(10px);
	background: ${ROW_BADGE_BACKGROUND};
	border-radius: 999px;
	box-sizing: border-box;
	display: flex;
	gap: 4px;
	height: 21px;
	padding: 4px 8px;

	& span {
		background: ${palette('white')};
		border-radius: 1px;
		height: ${p => (p.$isPlaying ? 'var(--bar-height)' : '2px')};
		transition: height 0.2s ease-in-out;
		width: 2px;
	}

	& span:nth-child(1) {
		--bar-height: 12px;
	}

	& span:nth-child(2) {
		--bar-height: 10px;
	}

	& span:nth-child(3) {
		--bar-height: 13px;
	}

	${p =>
		p.$isPlaying &&
		css`
			@media (prefers-reduced-motion: no-preference) {
				& span {
					animation: ${rise} 0.84s ease-in-out infinite alternate;
				}

				& span:nth-child(2) {
					animation-delay: 0.12s;
					animation-duration: 0.9s;
				}

				& span:nth-child(3) {
					animation-delay: 0.25s;
					animation-duration: 0.78s;
				}
			}
		`}
`;

// iOS FaveButton: a 44pt tap target around a 21pt bare heart, 12pt after the row text (the row's
// own gap), so the glyph centres 42pt from the trailing edge.
export const FaveHeartContainer = styled.div`
	flex-shrink: 0;
	height: 44px;
	width: 44px;

	&& button {
		box-sizing: border-box;
		min-width: 0;
		height: 44px;
		padding: 0;
		width: 44px;
	}

	// iOS draws the heart 20.8pt wide inside its 21pt frame. The asset keeps about 18% padding in its
	// viewBox, so the box is enlarged to put the drawn heart at that width.
	& svg {
		height: 25.25px;
		// The artwork sits right of centre in the asset's viewBox, so it is nudged back onto the box centre.
		transform: translateX(-2.9px);
		width: 25.25px;
	}

	// iOS's outline heart has a 1.9pt line, about 1.7 times the asset's. The extra width is stroked on
	// and the box shrunk to keep the drawn heart at 20.8pt. The faved heart is solid and keeps its size.
	&& button[aria-pressed='false'] svg {
		height: 24.3px;
		// Apple's heart is about 3% taller for its width than the asset's.
		transform: translateX(-2.45px) scaleY(1.03);
		width: 24.3px;
	}

	&& button[aria-pressed='false'] svg path {
		stroke: currentColor;
		stroke-linejoin: round;
		stroke-width: 3.1;
	}
`;
